import { PureComponent } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';

import { Repository } from 'src/components/types';
import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';
import { Ref } from 'src/sections/repository/types';
import urls from 'src/urls/source';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { FileMode } from '../types';

import messages from './file-mode-selector.i18n';

type FileModeLabelMap = { [key in FileMode]: JSX.Element };

type FileModeSelectorProps = RouteComponentProps & {
  atRef?: Ref;
  currentRef?: Ref;
  hash: string | null | undefined;
  mode: FileMode;
  path: string;
  repository: Repository | null | undefined;
};

class FileModeSelector extends PureComponent<FileModeSelectorProps> {
  fileModeLabels: FileModeLabelMap = {
    diff: <FormattedMessage {...messages.diffMode} />,
    history: <FormattedMessage {...messages.historyMode} />,
    source: <FormattedMessage {...messages.sourceMode} />,
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  publishFileAnalyticEvent = (label, repoUUID, workspaceUUID) => {
    publishUiEvent({
      source: 'file',
      action: 'clicked',
      actionSubject: 'dropdown',
      actionSubjectId: 'fileModeSelector',
      objectType: 'repository',
      objectId: repoUUID,
      containerType: 'workspace',
      containerId: workspaceUUID,
      attributes: {
        label,
        repository_uuid: repoUUID,
        frontbucket_view: true,
      },
    });
  };

  render() {
    const { atRef, currentRef, hash, history, mode, path, repository } =
      this.props;

    if (!repository || !hash) {
      return null;
    }

    const atRefName = atRef ? atRef.name : undefined;
    const currentRefName = currentRef ? currentRef.name : undefined;
    const at = atRefName || currentRefName || undefined;

    return (
      <DropdownMenu
        shouldRenderToParent
        trigger={({ triggerRef, ...props }) => (
          <Button
            {...props}
            ref={triggerRef}
            iconAfter={<ChevronDownIcon label="" />}
          >
            {this.fileModeLabels[mode]}
          </Button>
        )}
      >
        {mode !== 'source' && (
          <DropdownItem
            onClick={() => {
              this.publishFileAnalyticEvent(
                'source',
                uncurlyUuid(repository.uuid),
                repository?.workspace
                  ? uncurlyUuid(repository.workspace.uuid)
                  : ''
              );
              history.push(
                urls.ui.source(repository.full_name, {
                  refOrHash: hash,
                  path,
                  at,
                })
              );
            }}
          >
            {this.fileModeLabels.source}
          </DropdownItem>
        )}
        {mode !== 'diff' && (
          <DropdownItem
            onClick={() => {
              this.publishFileAnalyticEvent(
                'diff',
                uncurlyUuid(repository.uuid),
                repository?.workspace
                  ? uncurlyUuid(repository.workspace.uuid)
                  : ''
              );
              const revSpec = { destination: hash };
              history.push(
                urls.ui.diff(repository.full_name, path, revSpec, { at })
              );
            }}
          >
            {this.fileModeLabels.diff}
          </DropdownItem>
        )}
        {mode !== 'history' && (
          <DropdownItem
            onClick={() => {
              this.publishFileAnalyticEvent(
                'history',
                uncurlyUuid(repository.uuid),
                repository?.workspace
                  ? uncurlyUuid(repository.workspace.uuid)
                  : ''
              );
              history.push(
                urls.ui.fileHistory(repository.full_name, hash, path, { at })
              );
            }}
          >
            {this.fileModeLabels.history}
          </DropdownItem>
        )}
      </DropdownMenu>
    );
  }
}

export default withRouter(FileModeSelector);
